/**
 * Component: TransactionActivityChart
 * Area chart for monthly transaction activity.
 */

import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart/chart";
import { ExternalIcon } from "@/assets/svg";
import type { TransactionActivityData } from "../../../../domain/model/DashboardModel";

const chartConfig: ChartConfig = {
  value: {
    label: "Transaction",
    color: "#3b82f6",
  },
};

export interface TransactionActivityChartProps {
  data: TransactionActivityData | null;
}

export const TransactionActivityChart = ({ data }: TransactionActivityChartProps): React.JSX.Element => {
  const transactionActivityPoints = data?.points ?? [];

  if (!data || transactionActivityPoints.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <h3 className="m-0 text-slate-900 font-bold text-base leading-6">Transaction Activity</h3>
        </div>
        <div className="flex items-center justify-center h-[200px] text-gray-500">
          No transaction data available
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 pb-3 flex flex-col shadow-[0_10px_15px_-3px_rgba(15,23,42,0.05),0_4px_6px_-4px_rgba(15,23,42,0.03)]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="m-0 text-slate-900 font-bold text-base leading-6">Transaction Activity</h3>
        <button
          className="bg-transparent border-none p-0 cursor-pointer flex items-center justify-center hover:opacity-80 focus:outline-none active:opacity-60"
          aria-label="Open external link"
        >
          <ExternalIcon />
        </button>
      </div>
      <div className="relative w-full flex-1 min-h-[200px]">
	        <ChartContainer
          config={chartConfig}
          aria-label="Monthly transaction activity area chart"
          className="w-full h-full"
        >
	          <ResponsiveContainer width="100%" height="100%">
	            <AreaChart
	              data={transactionActivityPoints}
	              margin={{ top: 12, right: 12, bottom: 12, left: 0 }}
	            >
              <defs>
                <linearGradient id="fillValue" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="#3b82f6"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor="#3b82f6"
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="rgba(148, 163, 184, 0.15)"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fill: "#6b7280", fontSize: 12 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fill: "#9ca3af", fontSize: 11 }}
                tickFormatter={(value) => `${value / 1000}k`}
              />
              <ChartTooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#fillValue)"
                dot={{
                  fill: "#3b82f6",
                  strokeWidth: 2,
                  r: 4,
                  stroke: "#fff",
                }}
                activeDot={{
                  r: 6,
                  stroke: "#fff",
                  strokeWidth: 2,
                  fill: "#3b82f6",
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
};
